import Correspondance from "../../../image/icons/map/details/Correspondance.svg";
import Destination from "../../../image/icons/map/details/DestinationIcon.svg";
import MyPosition from "../../../image/icons/map/details/MyPositionIcon.svg";
import OneDash from "../../../image/icons/map/details/OneDashIcon.svg";
import BoatBadge from "../../../image/icons/map/transportBadges/BoatBadge.svg";
import BicycleIcon from "../../../image/icons/map/transportIcons/BicycleIcon.svg";
import BlaBlaCarIcon from "../../../image/icons/map/transportIcons/BlaBlaCarIcon.svg";
import BlaBlaDailyIcon from "../../../image/icons/map/transportIcons/BlaBlaDailyIcon.svg";
import BoatIcon from "../../../image/icons/map/transportIcons/BoatIcon.svg";
import BusIcon from "../../../image/icons/map/transportIcons/BusIcon.svg";
import CargoBicycleIcon from "../../../image/icons/map/transportIcons/CargoBicycleIcon.svg";
import CarIcon from "../../../image/icons/map/transportIcons/CarIcon.svg";
import HeLemanIcon from "../../../image/icons/map/transportIcons/HeLemanIcon.svg";
import MopedIcon from "../../../image/icons/map/transportIcons/MopedIcon.svg";
import ScooterSeatedIcon from "../../../image/icons/map/transportIcons/ScooterSeatedIcon.svg";
import ScooterStandingIcon from "../../../image/icons/map/transportIcons/ScooterStandingIcon.svg";
import SharingBikeIcon from "../../../image/icons/map/transportIcons/SharingBikeIcon3.svg";
import TrainIcon from "../../../image/icons/map/transportIcons/TrainIcon.svg";
import TramIcon from "../../../image/icons/map/transportIcons/TramIcon.svg";
import WalkingIcon from "../../../image/icons/map/transportIcons/WalkingIcon.svg";
import WalkIcon from "../../../image/icons/map/transportIcons/WalkingIcon.svg?react";
import { getTransportModeGroupColorHex } from "../../../tools/TransportModeColorsTools";
import {
    getRentalLegColorMode,
    getRentalVehicleDisplayGroup,
    getTransportModeGroupForItineraryLeg,
    type ItineraryLegTransportSignals,
    RentalVehicleDisplayGroup,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { RentalVehicleType } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { LegType } from "../../../types/itinerary/leg-type.enum";
import TransportModeBadgeIcon from "./TransportModeBadgeIcon";

const renderMonochromeIcon = (
    iconSrc: string,
    alt: string,
    mode: string,
    className: string,
) => (
    <span
        aria-label={alt}
        className={`inline-block shrink-0 ${ className }`}
        role="img"
        style={{
            "backgroundColor": getTransportModeGroupColorHex( mode ),
            "WebkitMask":      `url(${ iconSrc }) center / contain no-repeat`,
            "mask":            `url(${ iconSrc }) center / contain no-repeat`,
        }}
    />
);

const renderDefaultIcon = ( iconSrc: string, className: string ) => (
    <img
        className={className}
        src={iconSrc}
    />
);

type ItineraryTransportIconVariant = "plain" | "badge";

const DEFAULT_VERTICAL_CONNECTOR_COLOR = "#C7D2D9";

/**
 * Renders the vertical dashed connector used in the itinerary-details timeline.
 *
 * @param strokeColor - Stroke color for the rounded dash segments.
 * @returns Inline SVG matching {@link VerticalDashesIcon.svg} geometry.
 */
const renderVerticalConnectorDashes = ( strokeColor: string ) => (
    <svg
        aria-hidden={true}
        className="block"
        fill="none"
        height="27"
        viewBox="0 0 4 27"
        width="4"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M2 2L2 27"
            stroke={strokeColor}
            strokeDasharray="0 11.5"
            strokeLinecap="round"
            strokeWidth="4"
        />
    </svg>
);

const renderBadgeIcon = (
    mode: string,
    iconSrc: string,
    alt: string,
    badgeSizePx: number = 39,
    iconSizePx: number = 25,
) => (
    <TransportModeBadgeIcon
        alt={alt}
        badgeSizePx={badgeSizePx}
        iconSizePx={iconSizePx}
        iconSrc={iconSrc}
        mode={mode}
    />
);

const ItineraryTransportIcon = (
    mode: string,
    colorizeByMode: boolean = false,
    variant: ItineraryTransportIconVariant = "plain",
    rentalVehicleType?: string,
    itineraryLeg?: ItineraryLegTransportSignals,
    connectorColor: string = DEFAULT_VERTICAL_CONNECTOR_COLOR,
) => {
    const transportLeg: ItineraryLegTransportSignals = itineraryLeg ?? {
        "mode":   mode,
        "type":   LegType.publicTransport,
        "rental": rentalVehicleType !== undefined
            ? { "vehicle": { "type": rentalVehicleType as RentalVehicleType } }
            : undefined,
    };
    const modeLower = mode.toLowerCase();

    if ( modeLower === "myposition" ) {
        return ( <img
            className="w-[15px]"
            src={MyPosition}
                 /> );
    }

    if ( modeLower === "dashes" ) {
        return renderVerticalConnectorDashes( connectorColor );
    }

    if ( modeLower === "onedashe" ) {
        return (
            <img
                alt=""
                className="block"
                src={OneDash}
            />
        );
    }

    if ( modeLower === "corres" ) {
        return ( <img
            className="w-[15px]"
            src={Correspondance}
                 /> );
    }

    if ( modeLower === "destination" ) {
        return ( <img
            className="w-[15px]"
            src={Destination}
                 /> );
    }

    switch ( getTransportModeGroupForItineraryLeg( transportLeg ) ) {
        case TransportModeGroup.UNKNOWN:
            return null;

        case TransportModeGroup.UNSUPPORTED_LIFT:
            return null;

        case TransportModeGroup.CAR:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, CarIcon, "Car" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( CarIcon, "Car", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( CarIcon, "w-[25px]" );

        case TransportModeGroup.MY_CAR:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, CarIcon, "My car" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( CarIcon, "My car", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( CarIcon, "w-[25px]" );

        case TransportModeGroup.TRAIN:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, TrainIcon, "Train" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( TrainIcon, "Train", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( TrainIcon, "w-[25px]" );

        case TransportModeGroup.TRAM:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, TramIcon, "Tram" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( TramIcon, "Tram", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( TramIcon, "w-[25px]" );

        case TransportModeGroup.BUS:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, BusIcon, "Bus" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( BusIcon, "Bus", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( BusIcon, "w-[25px]" );

        case TransportModeGroup.BOAT:
            if ( variant === "badge" ) {
                return ( <img
                    alt="Boat"
                    className="w-[39px] h-[39px]"
                    src={BoatBadge}
                         /> );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( BoatIcon, "Boat", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( BoatIcon, "w-[25px]" );

        case TransportModeGroup.WALK:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, WalkingIcon, "Walk" );
            }

            return ( <>
                        <WalkIcon
                            className="w-[15px] inline-block align-middle shrink-0"
                            style={{ "color": colorizeByMode ? getTransportModeGroupColorHex( mode ) : "var(--itinerary-text-secondary, #334155)" }}
                        />
                        <span className="min-w-[1px]" />
                     </> );

        case TransportModeGroup.BICYCLE:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, BicycleIcon, "Bicycle" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( BicycleIcon, "Bicycle", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( BicycleIcon, "w-[15px]" );

        case TransportModeGroup.RENTAL:
        {
            const rentalVehicleTypeForIcon = transportLeg.rental?.vehicle?.type ?? rentalVehicleType;
            const rentalDisplayGroup = getRentalVehicleDisplayGroup( rentalVehicleTypeForIcon );
            let rentalIconSrc = SharingBikeIcon;
            let rentalAlt = "Sharing bike";
            const rentalColorMode = getRentalLegColorMode( transportLeg );
            let badgeSizePx = 45;
            let iconSizePx = 35;
            let plainClassName = "w-[29px] h-[29px]";
            let defaultClassName = "w-[45px]";

            switch ( rentalDisplayGroup ) {
                case RentalVehicleDisplayGroup.CARGO_BICYCLE:
                    rentalIconSrc = CargoBicycleIcon;
                    rentalAlt = "Vélo cargo";
                    badgeSizePx = 39;
                    iconSizePx = 25;
                    plainClassName = "w-[25px] h-[25px]";
                    defaultClassName = "w-[25px]";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_STANDING:
                    rentalIconSrc = ScooterStandingIcon;
                    rentalAlt = "Troninette";
                    badgeSizePx = 39;
                    iconSizePx = 25;
                    plainClassName = "w-[25px] h-[25px]";
                    defaultClassName = "w-[25px]";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_SEATED:
                    rentalIconSrc = ScooterSeatedIcon;
                    rentalAlt = "Trotinette assise";
                    badgeSizePx = 39;
                    iconSizePx = 25;
                    plainClassName = "w-[25px] h-[25px]";
                    defaultClassName = "w-[25px]";
                    break;

                case RentalVehicleDisplayGroup.CAR:
                    rentalIconSrc = CarIcon;
                    rentalAlt = "Voiture";
                    badgeSizePx = 39;
                    iconSizePx = 24;
                    plainClassName = "w-[25px] h-[25px]";
                    defaultClassName = "w-[25px]";
                    break;

                case RentalVehicleDisplayGroup.MOPED:
                    rentalIconSrc = MopedIcon;
                    rentalAlt = "Moto / Scooter";
                    badgeSizePx = 39;
                    iconSizePx = 24;
                    plainClassName = "w-[25px] h-[25px]";
                    defaultClassName = "w-[25px]";
                    break;

                default:
                    break;
            }

            if ( variant === "badge" ) {
                return renderBadgeIcon( rentalColorMode, rentalIconSrc, rentalAlt, badgeSizePx, iconSizePx );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( rentalIconSrc, rentalAlt, rentalColorMode, plainClassName );
            }

            return renderDefaultIcon( rentalIconSrc, defaultClassName );
        }

        case TransportModeGroup.BLABLADAILY:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, BlaBlaDailyIcon, "BlaBlaDaily", 35, 18 );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( BlaBlaDailyIcon, "BlaBlaDaily", mode, "w-[24px] h-[24px]" );
            }

            return renderDefaultIcon( BlaBlaDailyIcon, "w-[25px]" );

        case TransportModeGroup.BLABLACAR:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, BlaBlaCarIcon, "BlaBlaCar", 35, 18 );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( BlaBlaCarIcon, "BlaBlaCar", mode, "w-[23px] h-[23px]" );
            }

            return renderDefaultIcon( BlaBlaCarIcon, "w-[25px]" );

        case TransportModeGroup.HE_LEMAN:
            if ( variant === "badge" ) {
                return renderBadgeIcon( mode, HeLemanIcon, "HeLeman" );
            }

            if ( colorizeByMode ) {
                return renderMonochromeIcon( HeLemanIcon, "HeLeman", mode, "w-[25px] h-[25px]" );
            }

            return renderDefaultIcon( HeLemanIcon, "w-[25px]" );

        default:
            return null;
    }
};

export default ItineraryTransportIcon;
